import React from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentCheckIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ShieldExclamationIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

const TermsOfService = () => {
  const lastUpdated = "January 18, 2025";

  const sections = [
    {
      id: "acceptance",
      title: "Acceptance of Terms",
      icon: DocumentCheckIcon,
      content: [
        {
          subtitle: "Agreement to Terms",
          text: "By accessing and using INTRA CAPITAL LIMITED services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree to these terms, you must not use our services."
        },
        {
          subtitle: "Capacity to Contract",
          text: "You represent that you are at least 18 years old, have the legal capacity to enter into this agreement, and are not prohibited from using our services under applicable law."
        }
      ]
    },
    {
      id: "account-services",
      title: "Account and Services",
      icon: UserGroupIcon,
      content: [
        {
          subtitle: "Account Opening",
          text: "To use our trading services, you must open an account and provide accurate, complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account."
        },
        {
          subtitle: "Service Description",
          text: "We provide online trading services for stocks, options, futures, forex, and digital assets. Our services include trading platforms, market data, research tools, and customer support. Service availability may vary by jurisdiction and account type."
        },
        {
          subtitle: "Account Verification",
          text: "You must complete our verification process, including providing required documentation for identity verification and compliance with anti-money laundering (AML) and Know Your Customer (KYC) requirements."
        }
      ]
    },
    {
      id: "trading-risks",
      title: "Trading and Investment Risks",
      icon: ExclamationTriangleIcon,
      content: [
        {
          subtitle: "Risk Acknowledgment",
          text: "Trading in financial instruments involves substantial risk of loss and is not suitable for all investors. You acknowledge that you understand the risks involved and that you may lose some or all of your invested capital."
        },
        {
          subtitle: "Market Volatility",
          text: "Financial markets are subject to volatility, and past performance does not guarantee future results. Market conditions, economic events, and other factors can significantly impact the value of your investments."
        },
        {
          subtitle: "Leverage Risks",
          text: "If you trade on margin or use leveraged products, you may lose more than your initial investment. Leverage amplifies both potential gains and losses, and you should only trade with funds you can afford to lose."
        }
      ]
    },
    {
      id: "fees-charges",
      title: "Fees and Charges",
      icon: CurrencyDollarIcon,
      content: [
        {
          subtitle: "Trading Fees",
          text: "We charge fees for trading services, including commissions, spreads, and other transaction costs. Fee schedules are available on our website and may be updated from time to time with appropriate notice."
        },
        {
          subtitle: "Account Fees",
          text: "Additional fees may apply for account maintenance, inactivity, wire transfers, and other services. All applicable fees will be clearly disclosed before you incur them."
        },
        {
          subtitle: "Payment Terms",
          text: "All fees are due and payable when incurred. We may debit fees directly from your account balance or require separate payment depending on the service and fee type."
        }
      ]
    },
    {
      id: "prohibited-activities",
      title: "Prohibited Activities",
      icon: ShieldExclamationIcon,
      content: [
        {
          subtitle: "Illegal Activities",
          text: "You may not use our services for any illegal activities, including money laundering, fraud, market manipulation, or violation of securities laws and regulations."
        },
        {
          subtitle: "System Abuse",
          text: "You may not attempt to gain unauthorized access to our systems, interfere with our services, or engage in any activity that could harm our platform or other users."
        },
        {
          subtitle: "Misrepresentation",
          text: "You may not provide false or misleading information, impersonate others, or misrepresent your identity, qualifications, or investment experience."
        }
      ]
    },
    {
      id: "liability-disclaimers",
      title: "Liability and Disclaimers",
      icon: ScaleIcon,
      content: [
        {
          subtitle: "Service Availability",
          text: "While we strive to provide continuous service, we do not guarantee that our services will be available at all times. We may suspend or restrict access for maintenance, security, or regulatory reasons."
        },
        {
          subtitle: "Information Accuracy",
          text: "We provide market data and information for your convenience, but we do not guarantee its accuracy, completeness, or timeliness. You should verify all information before making trading decisions."
        },
        {
          subtitle: "Limitation of Liability",
          text: "To the maximum extent permitted by law, our liability is limited to the fees you have paid for our services. We are not liable for indirect, consequential, or punitive damages."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
              <ScaleIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            Legal terms and conditions governing your use of our services
          </p>
          <p className="text-sm text-gray-500">
            Last updated: {lastUpdated}
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8"
        >
          <h2 className="text-xl font-semibold text-blue-900 mb-3">Important Notice</h2>
          <p className="text-blue-800 leading-relaxed">
            These Terms of Service constitute a legally binding agreement between you and INTRA CAPITAL LIMITED. 
            Please read these terms carefully before using our services. By using our platform, you agree to 
            comply with and be bound by these terms and all applicable laws and regulations.
          </p>
        </motion.div>

        {/* Main Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                  <section.icon className="h-6 w-6 text-gray-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
              </div>
              
              <div className="space-y-4">
                {section.content.map((item, idx) => (
                  <div key={idx}>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.subtitle}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Termination Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-red-50 border border-red-200 rounded-lg p-6 mt-8"
        >
          <h2 className="text-2xl font-bold text-red-900 mb-4">Account Termination</h2>
          <div className="space-y-3 text-red-800">
            <p><strong>Your Right to Terminate:</strong> You may close your account at any time by contacting customer support. Outstanding positions must be closed and all fees paid before account closure.</p>
            <p><strong>Our Right to Terminate:</strong> We may suspend or terminate your account for violation of these terms, regulatory requirements, or risk management purposes with appropriate notice when possible.</p>
            <p><strong>Effect of Termination:</strong> Upon termination, your access to our services will cease, and you remain liable for all obligations incurred before termination.</p>
          </div>
        </motion.div>

        {/* Governing Law Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-purple-50 border border-purple-200 rounded-lg p-6 mt-8"
        >
          <h2 className="text-2xl font-bold text-purple-900 mb-4">Governing Law and Disputes</h2>
          <div className="space-y-3 text-purple-800">
            <p><strong>Governing Law:</strong> These terms are governed by the laws of England and Wales, without regard to conflict of law principles.</p>
            <p><strong>Dispute Resolution:</strong> Any disputes arising from these terms or our services shall be resolved through binding arbitration or in the courts of England and Wales.</p>
            <p><strong>Class Action Waiver:</strong> You agree to resolve disputes individually and waive the right to participate in class action lawsuits.</p>
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-8"
        >
          <div className="flex items-center mb-4">
            <PhoneIcon className="h-6 w-6 text-gray-600 mr-3" />
            <h2 className="text-xl font-bold text-gray-900">Questions About These Terms</h2>
          </div>
          <p className="text-gray-600 mb-4">
            If you have questions about these Terms of Service, please contact our legal department:
          </p>
          <div className="space-y-2 text-sm text-gray-600">
            <p><strong>Email:</strong> legal@intracapitals.com</p>
            <p><strong>Address:</strong> Britannia House, 503 1-11 Glenthorne Road, London, England, W6 0LH</p>
            <p><strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 5:00 PM GMT</p>
          </div>
        </motion.div>

        {/* Updates Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="text-center mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
        >
          <p className="text-sm text-yellow-800">
            <strong>Terms Updates:</strong> We may modify these Terms of Service from time to time. 
            Material changes will be communicated to you via email or platform notification at least 
            30 days before they take effect. Continued use of our services constitutes acceptance of updated terms.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService; 